/* eslint-disable local/ensure-wac-token-usage */
import type {
    AnchorHTMLAttributes,
    ButtonHTMLAttributes,
    DetailedHTMLProps,
    FC,
    MouseEvent
} from "react";

import { css, cssMap } from "@compiled/react";

import Spinner from "@atlaskit/spinner";

import { isLocalizableHref } from "@/libraries/web-framework";
import { DataEventProps } from "@/libraries/wpl/foundations/src";

type ButtonSize = "large" | "small";
export type ButtonColor = "blue" | "yellow" | "white" | "black" | "light_grey";
export type ButtonColorAgainst = "black" | "other";
export type ButtonVariant = "primary" | "secondary";

const getButtonSize = (size: string | undefined): ButtonSize => {
    const buttonSize = size;
    if (buttonSize && buttonSize === "small") {
        return "small";
    }
    return "large";
};

const getButtonVariant = (type: string | undefined): ButtonVariant => {
    const buttonType = type;
    if (buttonType === "secondary") {
        return "secondary";
    }
    return "primary";
};

const isButtonColor = (color: string): color is ButtonColor => {
    return (
        color === "blue" ||
        color === "yellow" ||
        color === "white" ||
        color === "black" ||
        color === "light_grey"
    );
};

const getButtonColor = (
    color: string | undefined,
    variant: ButtonVariant
): ButtonColor => {
    const buttonColor = color;
    if (buttonColor && isButtonColor(buttonColor)) {
        if (variant == "primary") {
            if (
                buttonColor == "blue" ||
                buttonColor == "yellow" ||
                buttonColor == "light_grey"
            )
                return buttonColor;
            else return "blue";
        } else {
            if (
                buttonColor == "black" ||
                buttonColor == "white" ||
                buttonColor == "light_grey"
            )
                return buttonColor;
            else return "black";
        }
    }

    return "blue";
};

const isColorAgainst = (color: string): color is ButtonColorAgainst => {
    return color === "black" || color === "other";
};

const getColorAgainst = (color: string | undefined): ButtonColorAgainst => {
    const colorAgainst = color;

    if (colorAgainst && isColorAgainst(colorAgainst)) {
        return colorAgainst;
    }

    return "black";
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const buttonPrimaryStyles: { [key: string]: any } = cssMap({
    primary_blue: {
        "color": "#ffffff",
        "backgroundColor": "#1868DB",
        "border": "none",
        "&:focus-visible": {
            outline: "2px solid #1868DB"
        },
        "&:hover": {
            backgroundColor: "#123263"
        }
    },
    primary_yellow: {
        "color": "#101214",
        "backgroundColor": "#FCA700",
        "border": "none",
        "&:focus-visible": {
            outline: "2px solid #1868DB"
        },
        "&:hover": {
            backgroundColor: "#FFE48F"
        }
    },
    primary_light_grey: {
        "color": "#101214",
        "backgroundColor": "#F8F8F8",
        "border": "none",
        "&:focus-visible": {
            outline: "2px solid #1868DB"
        },
        "&:hover": {
            backgroundColor: "#DDDEE1"
        }
    },
    secondary_white_against_black: {
        "color": "#ffffff",
        "backgroundColor": "transparent",
        "border": "2px solid #ffffff",
        "&:focus-visible": {
            outline: "2px solid #ffffff"
        },
        "&:hover": {
            backgroundColor: "#2B2C2F"
        }
    },
    secondary_white_against_other: {
        "color": "#ffffff",
        "backgroundColor": "transparent",
        "border": "2px solid #ffffff",
        "&:focus-visible": {
            outline: "2px solid #ffffff"
        },
        "&:hover": {
            backgroundColor: "#2B2C2F20",
            backgroundBlendMode: "multiply"
        }
    },
    secondary_black: {
        "color": "#101214",
        "backgroundColor": "transparent",
        "border": "2px solid #101214",
        "&:focus-visible": {
            outline: "2px solid #1868DB"
        },
        "&:hover": {
            backgroundColor: "#F0F1F2"
        }
    },
    secondary_light_grey: {
        "color": "#101214",
        "backgroundColor": "#F8F8F8",
        "border": "2px solid #101214",
        "&:focus-visible": {
            outline: "2px solid #1868DB"
        },
        "&:hover": {
            backgroundColor: "#DDDEE1"
        }
    }
});

const baseStyles = css({
    "alignItems": "center",
    "justifyContent": "center",
    "boxSizing": "border-box",
    "display": "inline-flex",
    "fontWeight": 500,
    "fontFamily": "var(--bxp-charlie-text-font-family)",
    "lineHeight": "1.5rem",
    "padding": "10px 24px",
    "textAlign": "center",
    "textDecoration": "none",
    "borderRadius": "40px",
    "transition": "200ms",
    "&:focus-visible": {
        outlineOffset: "4px"
    },
    "&:hover": {
        cursor: "pointer"
    }
});

const fontAndHeightStyles = cssMap({
    large: {
        fontSize: "1.25rem",
        lineHeight: "1.5rem",
        height: "min-content",
        minHeight: "44px",
        minWidth: "120px"
    },
    small: {
        fontSize: "1rem",
        lineHeight: "1.25rem",
        height: "min-content",
        minHeight: "40px",
        minWidth: "100px",
        maxWidth: "268px"
    }
});

const fullWidthStyles = css({
    width: "100%",
    maxWidth: "none"
});

const disabledStyles = css({
    ["&:hover"]: {
        cursor: "not-allowed",
        backgroundColor: "#B7B9BE",
        color: "#fff",
        border: "none"
    },
    backgroundColor: "#B7B9BE",
    color: "#fff",
    border: "none"
});

/**
 * @deprecated - Please use @/libraries/design/components/button instead
 */
export type Adg4ButtonProps = {
    size?: ButtonSize;
    variant?: "primary" | "secondary";
    colorAgainst?: "black" | "other";
    color?: ButtonColor;
    className?: string;
    dataEventProps?: DataEventProps;
    isLoading?: boolean;
    isFullWidth?: boolean;
    isDisabled?: boolean;
} & (
    | DetailedHTMLProps<
          ButtonHTMLAttributes<HTMLButtonElement>,
          HTMLButtonElement
      >
    | DetailedHTMLProps<
          AnchorHTMLAttributes<HTMLAnchorElement>,
          HTMLAnchorElement
      >
);

function isLinkArgs(
    props: unknown
): props is AnchorHTMLAttributes<HTMLAnchorElement> {
    return "href" in (props as AnchorHTMLAttributes<HTMLAnchorElement>);
}

const isSignInUrl = (href: string) => {
    const baseUrl = process.env["NEXT_PUBLIC_IDENTITY_BASE_URL"];
    const stargateBaseUrl = process.env["NEXT_PUBLIC_API_PRIVATE_BASE_URL"];
    const continueUrl = stargateBaseUrl + "/start/authredirect";
    const signInUrl = `${baseUrl}/login?continue=${encodeURIComponent(
        continueUrl
    )}`;

    // Check if href matches sign-in URL with or without application parameter, or is start-trial
    return (
        href === signInUrl ||
        href.startsWith(signInUrl + "&application=") ||
        href === "#start-trial"
    );
};

const isNonSigninOrWacUrl = (href: string) => {
    return !isLocalizableHref(href) && !isSignInUrl(href);
};

/**
 * @deprecated - Please use @/libraries/design/components/button instead
 */
/* eslint-disable complexity */
export const Adg4Button: FC<Adg4ButtonProps> = ({
    variant,
    color,
    colorAgainst,
    size,
    className,
    children,
    dataEventProps,
    isLoading,
    isDisabled = false,
    isFullWidth = false,
    ...props
}: Adg4ButtonProps) => {
    const isLink = isLinkArgs(props);
    const buttonVariant = getButtonVariant(variant);
    const buttonColor = getButtonColor(color, buttonVariant);
    const buttonColorAgainst = getColorAgainst(colorAgainst);
    const buttonSize: ButtonSize = getButtonSize(size);

    const styles =
        buttonVariant == "secondary" && buttonColor == "white"
            ? `${buttonVariant}_${buttonColor}_against_${buttonColorAgainst}`
            : `${buttonVariant}_${buttonColor}`;

    const variantStyles = buttonPrimaryStyles[styles];
    const fontAndHeightStyle = fontAndHeightStyles[buttonSize];

    const appearance =
        getButtonVariant(variant) === "secondary" ? "inherit" : "invert";

    const handleOnClick = (
        e: MouseEvent<HTMLAnchorElement> | MouseEvent<HTMLButtonElement>
    ) => {
        if (isDisabled) {
            e.preventDefault();
        } else {
            if (props.onClick) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                props.onClick(e as any);
            }
        }
    };

    return isLink ? (
        <a
            className={className}
            css={[
                baseStyles,
                variantStyles,
                fontAndHeightStyle,
                isFullWidth && fullWidthStyles,
                isDisabled && disabledStyles
            ]}
            {...props}
            {...dataEventProps}
            onClick={handleOnClick}
            href={props.href}
            target={
                typeof props.target === "string"
                    ? props.target
                    : props.href &&
                      /* redirecting to new tab if the href is not on WAC and not a Signin link */
                      isNonSigninOrWacUrl(props.href)
                    ? "_blank"
                    : ""
            }
            rel={
                props.href && isNonSigninOrWacUrl(props.href)
                    ? "noreferrer"
                    : ""
            }
        >
            {children}
        </a>
    ) : (
        <button
            className={className}
            disabled={isLoading || isDisabled}
            css={[
                baseStyles,
                variantStyles,
                fontAndHeightStyle,
                isFullWidth && fullWidthStyles,
                isDisabled && disabledStyles
            ]}
            {...(props as ButtonHTMLAttributes<HTMLButtonElement>)}
            {...dataEventProps}
            onClick={handleOnClick}
        >
            {isLoading ? (
                <Spinner
                    interactionName="load"
                    label="Loading"
                    appearance={appearance}
                />
            ) : (
                children
            )}
        </button>
    );
};
/* eslint-enable complexity */
